import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getUserProjects } from "@/app/actions/project/action";
import { ProjectLite } from "@/app/types/project/ProjectLite";
import { UCookie } from "@/app/utils/UCookie";
import { ULogger } from "@/app/utils/ULogger";
import { UProject } from "@/app/utils/UProject";
import { Globals } from "@/app/vars/Globals";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";

function saveCurrentProjectId(id?: number): void {
  if (id === undefined) {
    UCookie.remove(CookieKey.CURRENT_PROJECT_ID);
    return;
  }
  UCookie.write(CookieKey.CURRENT_PROJECT_ID, String(id), WORKSPACE_COOKIE_MAX_AGE);
}

function getSavedProjectId(): number | undefined {
  const raw = UCookie.read(CookieKey.CURRENT_PROJECT_ID);
  if (!raw) {
    return undefined;
  }
  const id = Number(raw);
  return Number.isNaN(id) ? undefined : id;
}

export type ProjectState = {
  currentProject?: ProjectLite;
  projects: ProjectLite[];
  projectsFetched: boolean;
  hydrateProjects(projects: ProjectLite[], savedId?: number): void;
  fetchUserProjects(): Promise<void>;
  setCurrentProject(project: ProjectLite): void;
  addProject(project: ProjectLite): void;
  updateProject(project: ProjectLite): void;
  removeProject(projectId: number): void;
  addBilledCallSeconds(projectId: number, seconds: number): void;
};

export const ProjectStore = create<ProjectState>()(
  devtools((set, getState) => ({
    currentProject: undefined,
    projects: [],
    projectsFetched: false,

    hydrateProjects(projects, savedId) {
      const next = UProject.resolveCurrent(projects, savedId);
      set({ projects, currentProject: next, projectsFetched: true });
      saveCurrentProjectId(next?.id);
    },

    async fetchUserProjects() {
      try {
        const result = await getUserProjects({
          page: 1,
          limit: Globals.USER_PROJECTS_LIMIT,
          hasNext: false,
          hasPrevious: false,
          totalPages: 0,
        });
        if (result) {
          const next =
            getState().currentProject ||
            UProject.resolveCurrent(result.projects, getSavedProjectId());
          set({ projects: result.projects, currentProject: next });
          saveCurrentProjectId(next?.id);
        }
      } catch (error) {
        ULogger.error("ProjectStore", "[ProjectStore] fetchUserProjects error:", error);
      } finally {
        set({ projectsFetched: true });
      }
    },

    setCurrentProject(project) {
      saveCurrentProjectId(project.id);
      set({ currentProject: project });
    },

    addProject(project) {
      const { projects } = getState();
      saveCurrentProjectId(project.id);
      set({ projects: [project, ...projects], currentProject: project });
    },

    updateProject(project) {
      const { currentProject, projects } = getState();
      set({
        currentProject: currentProject?.id === project.id ? project : currentProject,
        projects: projects.map((p) => (p.id === project.id ? project : p)),
      });
    },

    removeProject(projectId) {
      const { currentProject, projects } = getState();
      const remaining = projects.filter((p) => p.id !== projectId);
      const next = currentProject?.id === projectId ? remaining[0] : currentProject;
      saveCurrentProjectId(next?.id);
      set({ projects: remaining, currentProject: next });
    },

    addBilledCallSeconds(projectId, seconds) {
      const bump = (project: ProjectLite): ProjectLite =>
        project.id === projectId
          ? { ...project, callsBilledSeconds: project.callsBilledSeconds + Math.round(seconds) }
          : project;

      const { currentProject, projects } = getState();
      set({
        currentProject: currentProject ? bump(currentProject) : currentProject,
        projects: projects.map(bump),
      });
    },
  })),
);
